'use client';
import classNames from 'classnames';
import EcoIconCheckboxN from 'eco-design-micro/icons/eco-icon-checkbox-n';
import EcoIconCheckboxY from 'eco-design-micro/icons/eco-icon-checkbox-y';
import { DataLayerProps } from 'eco-design-micro/props/DataLayerProps';
import getLocal from 'eco-design-reliance/getLocal';
import gtm from 'eco-design-reliance/gtm';
import { useEffect, useState } from 'react';
import { sendMultiEmarsysEmail } from './apis';
import { termsTran, textTran } from './config';
import './index.scss';
import { CookieDataExtendType } from 'eco-design-reliance/cookies/type';

export const EmailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/; // 校验邮箱格式
export interface EmailTipsType {
    /**
     * 按钮文案
     */
    btnText?: string;
    /**
     * 输入框提示语
     */
    placeholder?: string;
    /**
     * 提交时文案为空的提示语
     */
    tipsEmpty?: string;
    /**
     * 邮箱格式错误提示语
     */
    tipsTypeError?: string;
    /**
     * 成功订阅提示语
     */
    successTips?: string;
    /**
     * 勾选提示语
     */
    tipsCheck?: string;
    /**
     * 没有选择产品时
     */
    tipsProductEmpty?: string;
    /**
     * 提交订阅失败时文案
     */
    tipsFailed?: string;
}
export interface EmailFieldsType {
    id: string;
    type: string;
    value: number | string | (string | number)[];
    overwrite: boolean;
}
export interface EmailProductType {
    value: number | string;
    label: string;
    checked?: boolean;
    fields: EmailFieldsType[];
}
export interface EcoEmailProps {
    /**
     * 样式名
     */
    theme?: 'blue';
    /**
     * 样式名
     */
    className?: string;
    /**
     * 产品选择标题
     */
    productTitle?: string;
    /**
     * 产品列表
     */
    productList?: EmailProductType[];
    /**
     * 提示语
     */
    tipsObj?: EmailTipsType;
    /**
     * 是否需要隐私协议
     */
    needTerms?: boolean;
    /**
     * 隐私协议的内容(不传入的话则会使用写好的一套)
     */
    termsHtml?: string;
    /**
     * 邮箱
     */
    email?: string;
    /**
     * contactListId
     */
    contactListId: string[];
    /**
     * fields 数组
     */
    fields?: EmailFieldsType[];
    /**
     * 隐私协议是否选中
     */
    defaultTermsCheck?: boolean;
    /**
     * 埋点
     */
    dataLayer?: DataLayerProps;
    /**
     * source 订阅来源
     */
    source?: string;
    /**
     * 接口发送前回调
     *
     * 如果在 onBeforeSendCallback return true,则不会执行自带的订阅接口
     *
     * data 用于接收输入框里的数据
     *
     * callback 用于执行成功后执行埋点发送
     */
    onBeforeSendCallback?: (
        data: {
            email: string;
            contactListId: string | string[];
            fields?: EmailFieldsType[];
        },
        gtmCallback?: (status: string) => void,
    ) => boolean | undefined;
    /**
     * 接口发送后回调
     */
    callback?: (type: 'success' | 'fail', options: any) => void;
    /**
     * 订阅数据
     */
    subscribeData?: CookieDataExtendType | undefined;
}
/**
 * 维护:谢保林
 *
 * 邮箱订阅
 */
export default function EcoEmail({
    theme = 'blue',
    className,
    productTitle,
    productList = [],
    tipsObj = {},
    needTerms = true,
    termsHtml,
    email,
    contactListId = [],
    fields,
    defaultTermsCheck = false,
    dataLayer,
    source,
    onBeforeSendCallback,
    callback,
    subscribeData,
}: EcoEmailProps) {
    const prefixCls = `eco-email`;
    const { locale = 'us' } = getLocal();
    const defaultFields = JSON.parse(JSON.stringify(fields || []));

    const toggleProductFieldHandleIn = (fieldsA: EmailFieldsType[], productList: EmailProductType[]) => {
        const productListC = JSON.parse(JSON.stringify(productList || [])); //重新复制一份
        const fieldsC: EmailFieldsType[] = JSON.parse(JSON.stringify(fieldsA || [])); //重新复制一份
        if (productListC.length === 0) {
            return fieldsC;
        }
        for (let i = 0; i < productListC.length; i++) {
            const productItem = productListC[i];
            const productFields = productItem.fields || []; //这是个数组哦

            if (!productItem.checked) {
                continue;
            }
            // 将所有选中的产品对应的 productFields 添加到 fieldsC
            for (let m = 0; m < productFields.length; m++) {
                const productFieldItem = productFields[m];
                fieldsC.push(productFieldItem);
            }
        }
        const fieldsD: EmailFieldsType[] = [];

        for (let k = 0; k < fieldsC.length; k++) {
            const itemA = fieldsC[k];
            const itemAValue1 = itemA.value;
            const itemAValIsArray = Array.isArray(itemAValue1);
            const itemAValue = itemAValIsArray ? itemAValue1 : [itemAValue1]; //全部转成数组统一处理
            const existItem = fieldsD.find((item: EmailFieldsType, index: number) => {
                return item.id === itemA.id;
            });

            if (!existItem) {
                fieldsD.push(itemA);
                continue;
            }
            const existItemValue1 = existItem.value;
            const existValIsArray = Array.isArray(existItemValue1);
            const existItemValue = existValIsArray ? existItemValue1 : [existItemValue1]; //全部转成数组统一处理

            for (let n = 0; n < itemAValue.length; n++) {
                const valueB = itemAValue[n];
                // 已存在了,不需要再次添加
                if (existItemValue.includes(valueB)) {
                    continue;
                }
                existItemValue.push(valueB);
            }
            if (existItemValue.length === 1) {
                existItem.value = existItemValue[0];
            } else {
                existItem.value = existItemValue;
            }
        }
        // 将 multiple 中的值强行换成数组
        const fieldsE: EmailFieldsType[] = [];
        for (let m = 0; m < fieldsD.length; m++) {
            const itemA = fieldsD[m];
            if (itemA.type === 'multiple' && typeof itemA.value !== 'object') {
                itemA.value = [itemA.value];
            }
            fieldsE.push(itemA);
        }
        return fieldsE;
    };
    const [fieldsB, setFieldsB] = useState<EmailFieldsType[]>([]);
    // 提示语-start
    const textObj = textTran[locale] || textTran['us'];
    const tipsObj2: EmailTipsType = {
        successTips: tipsObj.successTips || textObj.successTips, //文案-成功提示语
        btnText: tipsObj.btnText || textObj.btnText, // 文案--提交按钮
        placeholder: tipsObj.placeholder || textObj.placeholder, // 文案--输入框提示语
        tipsEmpty: tipsObj.tipsEmpty || textObj.tipsEmpty, // 文案--空时提示语
        tipsTypeError: tipsObj.tipsTypeError || textObj.tipsTypeError, // 文案--格式不对
        tipsProductEmpty: tipsObj.tipsProductEmpty || textObj.tipsProductEmpty, // 文案--产品没选择
        tipsCheck: tipsObj.tipsCheck || textObj.tipsCheck, //文案--没有勾选时提示语
        tipsFailed: tipsObj.tipsFailed || '',
    };
    // 提示语-end
    // 邮件-start
    const [emailTipsType, setEmailTipsType] = useState('');
    const [emailTipsMsg, setEmailTipsMsg] = useState('');
    const [emailValue, setEmailValue] = useState(email || '');
    const emailChangeHandle = (e: any) => {
        const value2 = e.target.value;
        setEmailValue(value2);
    };
    const emailBlurHandle = () => {
        checkEmailHandle(emailValue);
    };
    const checkEmailHandle = (value2: string) => {
        let isPass = true;

        if (!value2) {
            //邮箱非空校验
            setEmailTipsType('error');
            setEmailTipsMsg(tipsObj2.tipsEmpty || '');
            isPass = false;
        } else if (!EmailRegex.test(value2)) {
            //邮箱格式不对
            setEmailTipsType('error');
            setEmailTipsMsg(tipsObj2.tipsTypeError || '');
            isPass = false;
        } else {
            setEmailTipsType('');
            setEmailTipsMsg('');
        }
        return isPass;
    };
    // 邮件-end
    // 隐私协议-start
    const [termsTipsOpen, setTermsTipsOpen] = useState(false);
    const [termsCheck, setTermsCheck] = useState(defaultTermsCheck); // 隐私协议
    const termsHtmlB = termsHtml || termsTran[locale] || termsTran['us'];
    const termsChange = () => {
        const termsCheck2 = !termsCheck;
        setTermsCheck(termsCheck2);
        checkTermsHandle(termsCheck2);
    };
    const checkTermsHandle = (termsCheckC: boolean) => {
        let isPass = true;
        if (needTerms && !termsCheckC) {
            isPass = false;
        }
        if (isPass) {
            setTermsTipsOpen(false);
        } else {
            setTermsTipsOpen(true);
        }
        return isPass;
    };
    // 隐私协议-end
    // 产品选择-start
    const hasProduct = !!(productList || []).length;
    const [productTipsOpen, setProductTipsOpen] = useState(false);
    const [productListB, setProductListB] = useState<EmailProductType[]>(productList || []);
    const productItemChange = (item: EmailProductType, index: number) => {
        const productListC = JSON.parse(JSON.stringify(productListB));
        const flag = item.checked;
        productListC[index].checked = !flag;
        setProductListB(productListC);
        checkProductHandle(productListC); //执行检查
        toggleProductFieldHandle(productListC); //切换
    };
    const checkProductHandle = (productListC: EmailProductType[]) => {
        let isPass = false;
        // 没有产品列表时,通过检查
        if (productList.length === 0) {
            return true;
        }

        for (let i = 0; i < productListC.length; i++) {
            const itemA = productListC[i];
            if (itemA.checked) {
                isPass = true;
            }
        }
        if (isPass) {
            setProductTipsOpen(false);
        } else {
            setProductTipsOpen(true);
        }

        return isPass;
    };

    const toggleProductFieldHandle = (productListC: EmailProductType[]) => {
        const fieldsD = toggleProductFieldHandleIn(defaultFields, productListC);
        setFieldsB(fieldsD);
    };
    // 产品选择-end
    // 发送埋点-值
    const dataLayer2 = dataLayer || {
        event: 'uaEvent',
        event_name: 'activity_submit',
        button_name: tipsObj2.btnText,
    };
    // 发送埋点-内部
    const sendGtmHandle = (status: string) => {
        let tag = 'none';
        if (needTerms) {
            tag = 'Agree';
        }
        gtm.push({
            ...dataLayer2,
            status: status, //success|fail 提交状态
            tag: tag, //Agree|Disagree|None 用户是否同意隐私政策
        });
    };
    // 执行点击-start
    const clickSendHandle = async () => {
        // 执行邮箱校验
        const isPassEmail = checkEmailHandle(emailValue);
        if (!isPassEmail) {
            return;
        }
        // 执行产品选择校验
        const isPassProduct = checkProductHandle(productListB);
        if (!isPassProduct) {
            return;
        }
        // 执行隐私协议校验
        const isPassTerms = checkTermsHandle(termsCheck);
        if (!isPassTerms) {
            return;
        }
        if (!onBeforeSendCallback) {
            await sendSubmitHandle();
            return;
        }
        onBeforeSendCallback(
            {
                email: emailValue,
                contactListId: contactListId,
                fields: fieldsB,
            },
            (status: string) => {
                if (status === 'success') {
                    successHandle(true);
                } else if (status === 'fail') {
                    failHandle('');
                }
            },
        );
    };
    const sendSubmitHandle = async () => {
        const res = await sendSubmitList();
        if (res?.code === '0') {
            successHandle(true);
        } else {
            failHandle(res?.message);
        }
    };
    // 多个
    const sendSubmitList = async () => {
        const res: any = await sendMultiEmarsysEmail(
            locale,
            {
                contactListId: contactListId[0],
                email: emailValue,
                fields: fieldsB,
                source,
                ...(contactListId.length > 1 ? { otherContactListIds: contactListId.slice(1) } : {}),
            },
            subscribeData,
        );
        return res;
    };
    // 成功统一处理
    const successHandle = (sendGtmFlag: boolean) => {
        setEmailTipsType('success');
        setEmailTipsMsg(tipsObj2.successTips || ''); //成功
        setEmailValue(''); //邮箱清空
        if (callback) {
            callback('success', {
                btnText: tipsObj2.btnText,
            });
        }
        if (sendGtmFlag) {
            sendGtmHandle('success');
        }
    };
    // 失败统一处理
    const failHandle = (message: string) => {
        sendGtmHandle('fail');
        setEmailTipsType('error');
        setEmailTipsMsg(tipsObj2.tipsFailed || message || 'fail'); // 后台接口提示
        if (callback) {
            callback('fail', {
                btnText: tipsObj2.btnText,
            });
        }
    };
    // 执行点击-end
    const classes = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefixCls}-theme-${theme}`]: theme,
        [`${prefixCls}-product-error`]: productTipsOpen,
        [`${prefixCls}-terms-error`]: termsTipsOpen,
        [`${prefixCls}-email-${emailTipsType}`]: emailTipsType,
    });

    useEffect(() => {
        const fieldsA: EmailFieldsType[] = toggleProductFieldHandleIn(defaultFields, productList);
        setFieldsB(fieldsA);
        // 不加JSON.stringify的话,会导致无限循环,内存泄漏
    }, []);

    return (
        <div className={classes}>
            {hasProduct && (
                <>
                    {productTitle && <div className={`${prefixCls}-product-title`}>{productTitle}</div>}
                    <div className={`${prefixCls}-product-list`}>
                        {productListB.map((item: EmailProductType, index: number) => {
                            return (
                                <div
                                    className={`${prefixCls}-product-item`}
                                    key={index}
                                    onClick={() => {
                                        productItemChange(item, index);
                                    }}
                                >
                                    <div className={`${prefixCls}-product-item-img`}>
                                        {item.checked && <EcoIconCheckboxY />}
                                        {!item.checked && <EcoIconCheckboxN />}
                                    </div>
                                    <div className={`${prefixCls}-product-item-msg`}>{item.label}</div>
                                </div>
                            );
                        })}
                    </div>
                    <div className={`${prefixCls}-error ${prefixCls}-product-tips-error`}>{tipsObj2.tipsProductEmpty}</div>
                </>
            )}
            <div className={`${prefixCls}-in`}>
                <div className={`${prefixCls}-in-c`}>
                    <input
                        type="text"
                        className={`${prefixCls}-in-input`}
                        value={emailValue}
                        placeholder={tipsObj2.placeholder}
                        onChange={emailChangeHandle}
                        onBlur={emailBlurHandle}
                    />
                </div>
                <div className={`${prefixCls}-in-r`} onClick={clickSendHandle}>
                    {tipsObj2.btnText}
                </div>
            </div>
            <div className={`${prefixCls}-error ${prefixCls}-email-tips-error`}>{emailTipsMsg}</div>

            {needTerms && (
                <>
                    <div className={`${prefixCls}-terms`} onClick={termsChange}>
                        <div className={`${prefixCls}-terms-img`}>
                            {termsCheck && <EcoIconCheckboxY />}
                            {!termsCheck && <EcoIconCheckboxN />}
                        </div>
                        <div
                            className={`${prefixCls}-terms-msg`}
                            dangerouslySetInnerHTML={{
                                __html: termsHtmlB,
                            }}
                        ></div>
                    </div>
                    <div className={`${prefixCls}-error ${prefixCls}-terms-tips-error`}>{tipsObj2.tipsCheck}</div>
                </>
            )}
        </div>
    );
}
